import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, IconButton,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Tooltip, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { Add, Edit, Delete, Close, Event, CalendarToday, Category, LocationOn, AccessTime, People } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const categories = ['Academic', 'Chapel', 'Sports', 'Fundraiser', 'Holiday', 'Parent Meeting', 'Field Trip', 'Other'];

const mockEvents = [
  { id: 1, title: 'Parent-Teacher Conferences', category: 'Parent Meeting', date: '2024-01-25', endDate: '2024-01-26', time: '3:00 PM – 7:00 PM', location: 'Individual Classrooms', audience: 'Parents', description: 'Scheduled parent-teacher conferences for all grades.', status: 'upcoming' },
  { id: 2, title: 'Science Fair', category: 'Academic', date: '2024-02-01', time: '9:00 AM – 3:00 PM', location: 'School Gymnasium', audience: 'All', description: 'Annual science fair for grades 4–8.', status: 'upcoming' },
  { id: 3, title: 'Friday Chapel Service', category: 'Chapel', date: '2024-01-26', time: '2:30 PM', location: 'Main Auditorium', audience: 'All', description: 'Weekly chapel service with Pastor James.', status: 'upcoming' },
  { id: 4, title: 'Winter Break', category: 'Holiday', date: '2024-02-15', endDate: '2024-02-19', time: 'All Day', location: 'N/A', audience: 'All', description: 'School closed for Winter Break. Resumes February 22.', status: 'upcoming' },
  { id: 5, title: 'Natural History Museum Field Trip', category: 'Field Trip', date: '2024-02-05', time: '8:00 AM – 4:00 PM', location: 'Natural History Museum, Austin', audience: '5th Grade', description: 'Field trip for 5th grade science classes.', status: 'upcoming' },
  { id: 6, title: 'Basketball Game vs. Grace Academy', category: 'Sports', date: '2024-01-30', time: '5:00 PM', location: 'Eagles Gymnasium', audience: 'All', description: 'Home basketball game. Admission is free.', status: 'upcoming' },
  { id: 7, title: 'Annual Fundraiser Gala', category: 'Fundraiser', date: '2024-03-08', time: '6:00 PM – 10:00 PM', location: 'Marriott Hotel, Austin', audience: 'Parents & Staff', description: 'Annual fundraiser dinner to support school programs.', status: 'upcoming' },
  { id: 8, title: 'Thanksgiving Chapel', category: 'Chapel', date: '2023-11-21', time: '10:00 AM', location: 'Main Auditorium', audience: 'All', description: 'Special Thanksgiving chapel service.', status: 'past' },
];

const audienceColors = { All: 'primary', Students: 'secondary', Parents: 'info', 'Parents & Staff': 'warning', 'Staff': 'default', '5th Grade': 'success' };

const EventsAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/calendar')) return 1;
    if (location.pathname.includes('/categories')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [events, setEvents] = useState(mockEvents);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [form, setForm] = useState({ title: '', category: '', date: '', time: '', location: '', audience: 'All', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const upcomingEvents = events.filter(e => e.status === 'upcoming').sort((a, b) => a.date.localeCompare(b.date));
  const pastEvents = events.filter(e => e.status === 'past');

  const handleSave = () => {
    if (dialog.item) {
      setEvents(prev => prev.map(e => e.id === dialog.item.id ? { ...e, ...form } : e));
      setSnackbar({ open: true, message: 'Event updated' });
    } else {
      setEvents(prev => [...prev, { id: Date.now(), ...form, status: 'upcoming' }]);
      setSnackbar({ open: true, message: 'Event created' });
    }
    setDialog({ open: false, item: null });
    setForm({ title: '', category: '', date: '', time: '', location: '', audience: 'All', description: '' });
  };

  const handleDelete = (id) => {
    setEvents(prev => prev.filter(e => e.id !== id));
    setSnackbar({ open: true, message: 'Event deleted' });
  };

  const openEdit = (item) => {
    setForm({ title: item.title, category: item.category, date: item.date, time: item.time, location: item.location, audience: item.audience, description: item.description });
    setDialog({ open: true, item });
  };

  // Group upcoming events by month for calendar view
  const grouped = {};
  upcomingEvents.forEach(e => {
    const month = new Date(e.date).toLocaleString('default', { month: 'long', year: 'numeric' });
    if (!grouped[month]) grouped[month] = [];
    grouped[month].push(e);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Events & Calendar</Typography>
          <Typography variant="body2" color="text.secondary">{upcomingEvents.length} upcoming events</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialog({ open: true, item: null })}>Add Event</Button>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<Event />} label="All Events" iconPosition="start" />
            <Tab icon={<CalendarToday />} label="Calendar View" iconPosition="start" />
            <Tab icon={<Category />} label="Categories" iconPosition="start" />
          </Tabs>
        </Box>

        {/* All Events Tab */}
        {tab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Audience</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...upcomingEvents, ...pastEvents].map(evt => (
                  <TableRow key={evt.id} hover sx={{ opacity: evt.status === 'past' ? 0.6 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{evt.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{evt.description.slice(0, 60)}...</Typography>
                    </TableCell>
                    <TableCell><Chip label={evt.category} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{evt.date}</Typography>
                      {evt.endDate && <Typography variant="caption" color="text.secondary">– {evt.endDate}</Typography>}
                    </TableCell>
                    <TableCell>{evt.time}</TableCell>
                    <TableCell>{evt.location}</TableCell>
                    <TableCell>
                      <Chip label={evt.audience} size="small" color={audienceColors[evt.audience] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(evt)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(evt.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Calendar View Tab */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            {Object.entries(grouped).map(([month, evts]) => (
              <Box key={month} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>{month}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {evts.map(evt => (
                    <Paper key={evt.id} variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ textAlign: 'center', minWidth: 52, bgcolor: 'primary.50', borderRadius: 1, p: 1 }}>
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                          {new Date(evt.date).toLocaleString('default', { month: 'short' })}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                          {new Date(evt.date).getDate() + 1}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{evt.title}</Typography>
                          <Chip label={evt.category} size="small" variant="outlined" />
                          <Chip label={evt.audience} size="small" color={audienceColors[evt.audience] || 'default'} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{evt.time}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{evt.location}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEdit(evt)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(evt.id)}><Delete fontSize="small" /></IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Categories Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Event Categories</Typography>
              <Button variant="outlined" size="small" startIcon={<Add />}>Add Category</Button>
            </Box>
            <Grid container spacing={2}>
              {categories.map(cat => {
                const count = events.filter(e => e.category === cat).length;
                return (
                  <Grid item xs={12} sm={6} md={4} key={cat}>
                    <Card elevation={1} sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{cat}</Typography>
                          <Typography variant="body2" color="text.secondary">{count} event{count !== 1 ? 's' : ''}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small"><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" disabled={count > 0}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Add/Edit Event Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {dialog.item ? 'Edit Event' : 'Add New Event'}
          <IconButton size="small" onClick={() => setDialog({ open: false, item: null })}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField label="Event Title" fullWidth value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={form.category} label="Category" onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Audience</InputLabel>
                <Select value={form.audience} label="Audience" onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}>
                  {['All', 'Students', 'Parents', 'Staff', 'Parents & Staff'].map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}><TextField type="date" label="Start Date" fullWidth InputLabelProps={{ shrink: true }} value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField label="Time" fullWidth placeholder="e.g. 3:00 PM – 5:00 PM" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField label="Location" fullWidth value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField label="Description" multiline rows={3} fullWidth value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.title || !form.date}>{dialog.item ? 'Save Changes' : 'Create Event'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default EventsAdmin;
